<template>
  <div class="orchard-timeline" ref="scrollEl">
    <div class="orchard-bg-fixed"></div>
    <div class="orchard-bg-mask"></div>
    <div
      v-for="group in groupedMoments"
      :key="group.date"
      class="timeline-item"
    >
      <div class="timeline-row">
        <span class="timeline-dot"></span>
        <span class="moment-date-label">{{ formatDate(group.date) }}</span>
      </div>
      <span class="timeline-line"></span>
      <div class="moment-group-block">
        <OrchardMoment
          v-for="(moment, momentIndex) in group.moments"
          :key="moment.id"
          :moment="moment"
          :momentIndex="getMomentGlobalIndex(group, momentIndex)"
          @comment="handleMomentComment"
          @update-like="handleMomentLike"
        />
      </div>
    </div>

    <div class="load-footer">
      <div v-if="loading" class="loading-text">加载中...</div>
      <div v-else-if="errorMsg" class="error-text">{{ errorMsg }}</div>
      <div v-else-if="finished" class="no-more">枝头果已摘尽 但爱是无尽的 💞</div>
      <div v-else class="loading-text">上滑继续加载</div>
      <div ref="sentinel" class="load-sentinel"></div>
    </div>
    <OrchardPublish @publish="handlePublish" />
  </div>
</template>

<script>
import OrchardMoment from '@/components/OrchardMoment.vue'
import OrchardPublish from '@/components/OrchardPublish.vue'
import { ImagePreview } from 'vant'
import { getGroupedMoments } from '@/api/orchard'

function formatDate (dateStr) {
  const d = new Date(dateStr)
  const m = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  return `${m}月${day}日`
}

export default {
  name: 'OrchardView',
  components: { OrchardMoment, OrchardPublish },
  data () {
    return {
      moments: [],
      groupedMoments: [],
      page: 1,
      size: 8,
      loading: false,
      finished: false,
      errorMsg: '',
      observer: null
    }
  },
  mounted () {
    this.initObserver()
    this.resetList()
  },
  beforeDestroy () {
    this.destroyObserver()
  },
  methods: {
    formatDate,
    previewImages (images, idx) {
      ImagePreview({ images, startPosition: idx })
    },
    initObserver () {
      this.destroyObserver()
      const sentinel = this.$refs.sentinel
      if (!sentinel) return
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[0]
        if (entry && entry.isIntersecting) {
          this.onLoad()
        }
      }, { threshold: 0.1 })
      this.observer.observe(sentinel)
    },
    destroyObserver () {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    async resetList () {
      this.page = 1
      this.moments = []
      this.groupedMoments = []
      this.finished = false
      this.errorMsg = ''
      this.onLoad()
    },
    async onLoad () {
      // 防止瞬时多次触发导致连续分页
      if (this.loading || this.finished) return
      this.loading = true
      try {
        const res = await getGroupedMoments({ page: this.page, size: this.size })
        const data = res?.data || {}
        const list = Array.isArray(data.records) ? data.records : []
        const total = Number(data.total || data.postCount || 0)
        const totalPages = total > 0 ? Math.ceil(total / this.size) : null

        // 判定是否到底
        if (list.length < this.size || (totalPages && this.page >= totalPages)) {
          this.finished = true
        }

        // 合并并分组
        this.moments = this.moments.concat(list)
        const grouped = {}
        this.moments.forEach(item => {
          const date = (item && item.createdAt) ? item.createdAt.slice(0, 10) : '未知日期'
          if (!grouped[date]) grouped[date] = []
          grouped[date].push(item)
        })
        this.groupedMoments = Object.keys(grouped)
          .sort((a, b) => new Date(b) - new Date(a))
          .map(date => ({ date, moments: grouped[date] }))

        this.page++
        this.errorMsg = ''
      } catch (e) {
        // 网络或数据异常，停止后续触发，避免一直处于加载态
        this.finished = true
        this.errorMsg = '网络异常，请稍后再试'
        this.$toast && this.$toast(this.errorMsg)
      } finally {
        this.loading = false
      }
    },
    handlePublish (data) {
      this.$toast('已收到发布内容，后续可对接后端并刷新列表！')
      this.resetList()
    },
    handleMomentComment ({ moment, comment }) {
      if (Array.isArray(moment.comments)) {
        moment.comments.push(comment)
      } else {
        this.$set(moment, 'comments', [comment])
      }
    },
    handleMomentLike ({ moment, liked }) {
      const userId = localStorage.getItem('userId')
      const nickname = localStorage.getItem('nickname') || '我'
      if (!Array.isArray(moment.likeUsers)) {
        this.$set(moment, 'likeUsers', [])
      }
      if (liked) {
        // 点赞，push 当前用户
        if (!moment.likeUsers.some(u => String(u.userId) === String(userId))) {
          moment.likeUsers.push({ userId, nickname })
        }
      } else {
        // 取消点赞，移除当前用户
        moment.likeUsers = moment.likeUsers.filter(u => String(u.userId) !== String(userId))
      }
    },
    // 🎯 计算moment在全局的索引位置
    getMomentGlobalIndex (group, momentIndex) {
      const currentGroupIndex = this.groupedMoments.findIndex(g => g.date === group.date)
      let globalIndex = 0

      // 计算前面所有组的moment总数
      for (let i = 0; i < currentGroupIndex; i++) {
        globalIndex += this.groupedMoments[i].moments.length
      }

      // 加上当前组内的索引
      globalIndex += momentIndex

      return globalIndex
    }
  }
}
</script>

<style scoped>
/* 背景配置 - 方便后续更换 */
:root {
  --orchard-bg-image: url('~@/assets/orchard/orchard.jpg');
  --orchard-bg-overlay: linear-gradient(
    to bottom,
    rgba(255,255,255,0.25) 0%,
    rgba(255,255,255,0.35) 50%,
    rgba(255,255,255,0.30) 100%
  );
}

.orchard-timeline {
  min-height: 100vh;
  padding: 8px 24px 32px 24px;
  max-width: 100vw;
  box-sizing: border-box;
  padding-top: 56px;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;

  /* 🚀 苹果级滚动优化 */
  scroll-behavior: smooth; /* 平滑滚动 */
  -webkit-overflow-scrolling: touch; /* iOS 弹性滚动 */
  overscroll-behavior: contain; /* 防止过度滚动 */
  scrollbar-width: none; /* Firefox 隐藏滚动条 */
  -ms-overflow-style: none; /* IE/Edge 隐藏滚动条 */
}

/* Webkit浏览器隐藏滚动条 */
.orchard-timeline::-webkit-scrollbar {
  display: none;
}

.orchard-bg-fixed {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  background: var(--orchard-bg-image) center center / cover no-repeat;
  background-attachment: fixed;
  pointer-events: none;

  /* 🎯 GPU硬件加速优化 */
  will-change: transform; /* 提示浏览器即将变换 */
  transform: translateZ(0); /* 强制GPU加速 */
  backface-visibility: hidden; /* 隐藏背面，提高性能 */
}

/* 🎨 精简timeline项目样式 */
.timeline-item {
  position: relative;
  margin-bottom: 26px;
}

.orchard-bg-mask {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: var(--orchard-bg-overlay);
  z-index: 1;
  pointer-events: none;

  /* 🎯 GPU硬件加速优化 */
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}

@media (max-width: 600px) {
  .orchard-timeline {
    padding-top: 52px;
  }
}

/* 时间线样式 */

.timeline-row {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  height: 24px;
}

.timeline-dot {
  width: 14px;
  height: 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  margin-right: 10px;
  flex-shrink: 0;
  z-index: 2;
  position: relative;
  left: 0px;

  /* 🎯 时间点优化 */
  transform: translateZ(0); /* GPU加速 */
  will-change: transform;
  backface-visibility: hidden;
}

.timeline-line {
  position: absolute;
  left: 6px;
  top: 26px;
  width: 0;
  bottom: 0;
  height: auto;
  border-left: 1px dashed rgba(102, 126, 234, 0.3);
  background: none;
  z-index: 1;
  border-radius: 1px;
  pointer-events: none;
}

.moment-date-label {
  display: inline-block;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(16px) saturate(180%);
  color: #667eea;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 20px;
  padding: 6px 16px;
  letter-spacing: 1px;
  margin-right: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.5);

  /* 🚀 日期标签优化 */
  transform: translateZ(0); /* GPU加速 */
  will-change: transform;
}

.moment-group-block {
  margin-left: 24px;
  width: 100%;

  /* ⚡ 内容块优化 */
  transform: translateZ(0); /* GPU加速 */
  will-change: transform;
}

.moment-nick {
  font-size: 1rem;
  font-weight: 600;
  color: #667eea;
}

.moment-content {
  font-size: 1.1rem;
  font-weight: 400;
  color: #34495e;
  margin-bottom: 14px;
  line-height: 1.75;
  word-break: break-all;
  letter-spacing: 0.2px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', '微软雅黑', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  text-shadow: 0 0.5px 1px rgba(0, 0, 0, 0.05);
  opacity: 0.95;
}

.moment-images-grid {
  display: grid;
  grid-template-columns: repeat(3, 90px);
  gap: 8px;
  margin-bottom: 12px;
  max-width: 290px;
}

.moment-img-wrap {
  border-radius: 10px;
  overflow: hidden;
  background: #f7fafc;
  border: 1px solid rgba(160, 174, 192, 0.2);
  width: 90px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.moment-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.moment-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  margin-bottom: 2px;
}

.moment-time {
  color: #a0aec0;
  font-size: 0.85rem;
  font-weight: 400;
  letter-spacing: 0.5px;
}

.moment-likes {
  color: #667eea;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 2px;
}

.like-nick {
  font-weight: 500;
  color: #667eea;
}

.moment-comments {
  margin-top: 8px;
}

.moment-comment-bubble {
  background: transparent;
  color: #718096;
  border-radius: 0;
  padding: 2px 0;
  font-size: 0.85rem;
  margin-bottom: 2px;
  display: inline-block;
  line-height: 1.4;
  border: none;
}

.comment-nick {
  font-weight: 500;
  color: #9ca3af;
  margin-right: 4px;
}

::v-deep .van-icon[name="like"],
::v-deep .van-icon[name="like-o"],
::v-deep .van-icon[name="comment-o"] {
  color: #9ca3af !important;
}

.no-more {
  text-align: center;
  color: #a0aec0;
  font-size: 0.85rem;
  margin: 0 auto;
  z-index: 10;
  position: relative;
  opacity: 0.8;
}

/* 全局滚动性能优化 */
* {
  -webkit-font-smoothing: antialiased; /* 字体反锯齿 */
  -moz-osx-font-smoothing: grayscale; /* macOS字体优化 */
}

/* 时间线核心元素GPU加速 */
.timeline-row,
.timeline-line,
.moment-images-grid {
  transform: translateZ(0);
  will-change: transform;
}

/*
  如何更换背景图：
  只需要修改 :root 中的 --orchard-bg-image 变量即可
  例如：
  --orchard-bg-image: url('path/to/your/new-image.jpg');
  或者：
  --orchard-bg-image: linear-gradient(135deg, #667eea, #764ba2);
*/
</style>
